import React, { useState } from "react";
import {
ArrowRight,
Activity,
ShieldCheck,
CalendarDays,
RadioTower,
MapPin,
Mail,
Menu,
X,
CheckCircle2,
ChevronRight,

} from "lucide-react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Modal,
  Paper,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import InsightsIcon from "@mui/icons-material/Insights";
import MemoryIcon from "@mui/icons-material/Memory";
import { baseURL } from "../Config/config";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import SecurityIcon from "@mui/icons-material/Security";
import TuneIcon from "@mui/icons-material/Tune";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { create } from "../Store/projectPermission/permssionReducer";
import XIcon from "@mui/icons-material/X";
import LogoutIcon from "@mui/icons-material/Logout";
import selexpyramis1 from "../images/products/selexsuite_product.jpeg"
import SelexMB from "../images/SelexMB Final.png";
import SelexSIM from "../images/SelexSIM.png";
import SelexRISK from "../images/SelexRISK.png";

import Footer from "./Footer";
import SelexRisk from "./SelexRisk";


const primary = "#0078c9";
const accent = "#9e0b22";
const navy = "#061d34";
const bodyText = "#64748b";
const border = "rgba(0,120,201,0.14)";
const cardBackground =
  "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,251,255,0.94))";

const solutions = [
  {
    title: "Soft Sensors & Virtual Analyzers",
    description:
      "Predict lab-quality properties in real time — sour-water NH₃/H₂S, product specifications, and emissions — without waiting on the analyzer.",
    icon: <MonitorHeartIcon />,
  },
  {
    title: "Hybrid Online Advisory",
    description:
      "Combine first-principles models with machine-learning correction to guide operators toward the next best action on live units.",
    icon: <MemoryIcon />,
  },
  {
    title: "Predictive Reliability",
    description:
      "Use FMECA, RPN, and remaining-life analytics to surface developing failures before they become shutdowns.",
    icon: <InsightsIcon />,
  },
  {
    title: "Digital Twin & Optimization",
    description:
      "Maintain a live unit twin that tunes setpoints against margin, energy, operating limits, and changing process conditions.",
    icon: <TuneIcon />,
  },
  {
    title: "Consequence & Risk Intelligence",
    description:
      "Connect flare, dispersion, and fire modelling to your risk matrix — from PSV relief load through escalation analysis.",
    icon: <SecurityIcon />,
  },
  {
    title: "Network & Asset Optimization",
    description:
      "Optimize wells, separators, pipelines, and connected assets across the field while accounting for production and pressure constraints.",
    icon: <DeviceHubIcon />,
  },
];

const platforms = [
  {
    code: "SelexMB™",
    title: "Machine & Asset Data",
    logo: SelexMB,
    description:
      "Turn historian and asset data into models your operators can trust, then deploy, monitor, and improve them.",
    items: [
      "Data-science and ML workbench",
      "Soft sensors and classifiers",
      "Deployment, case study, and what-if analysis",
    ],
  },
  {
    code: "SelexSIM™",
    title: "Model, Simulate & Optimize",
    logo: SelexSIM,
    description:
      "Fuse rigorous thermodynamics with data-driven correction and live, multi-objective optimization.",
    items: [
      "Digital twin and process studio",
      "PR / SRK equation-of-state engines",
      "Constraint-aware optimization",
    ],
  },
  {
    code: "SelexRISK™",
    title: "Process Safety & Risk",
    logo: SelexRISK,
    description:
      "Perform HAZOP, relief, and consequence analysis using methods aligned with API 521, ISO, and NORSOK.",
    items: [
      "Hybrid HAZOP and LOPA",
      "Flare, fire, and dispersion modelling",
      "FMECA and criticality assessment",
    ],
  },
];

const approachSteps = [
  {
    number: "01",
    title: "Capture",
    description:
      "Encode domain knowledge and the real operating constraints of the unit.",
  },
  {
    number: "02",
    title: "Operationalize",
    description:
      "Train, validate, and version models through a disciplined MLOps workflow.",
  },
  {
    number: "03",
    title: "Deploy",
    description:
      "Fuse machine learning with first principles and deliver software your team owns.",
  },
  {
    number: "04",
    title: "Sustain",
    description:
      "Monitor drift and recalibrate against live data throughout the asset lifecycle.",
  },
];

const stats = [
  ["25+", "Products in the SelexSuite"],
  ["30+", "Successfull projects Deployments"],
  ["Global", "Standards & Best Practices"],
  ["One Platform", "AI • Simulation • Risk"],
];

const industries = [
  "Oil & Gas",
  "Refining",
  "Gas Processing",
  "LNG",
  "Petrochemicals",
  "Sulphur Recovery",
];

const sectionLabelSx = {
  color: primary,
  fontSize: 25,
  fontWeight: 950,
  letterSpacing: 1.2,
  mb: 1.2,
};


const navLinkSx = {
  color: bodyText,
  fontWeight: 800,
  fontSize: 14,
  textDecoration: "none",
  transition: "0.2s",
  "&:hover": { color: primary },
};

const COLORS = {
  primary: "#0078c9",
  accent: "#9e0b22",
  navy: "#062a4f",
  dark: "#071827",
  muted: "#64748b",
};

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Solutions", href: "#solutions" },
  { label: "Platform", href: "#platform" },
  { label: "Approach", href: "#approach" },
  { label: "Contact", href: "#contact" },
];

function Header({isLoggedIn,setIsLoggedIn}) {
  const [open, setOpen] = useState(false);

  const closeMenu = () => setOpen(false);
  const navigate = useNavigate();
    const dispatch = useDispatch();
   function logout() {
      sessionStorage.removeItem("usertoken");
      sessionStorage.removeItem("projectID");
      sessionStorage.removeItem("projectName");
      setIsLoggedIn(false);

  
      dispatch(create([{ empty: true }]));
  
      navigate("/");      
    }

  return (
    <header className="site-header">
      <a href="#hero" className="brand" onClick={closeMenu}>
        <img src="images/LandingPage/logo.png" alt="SelexSuite™" />
      </a>

      <nav className={open ? "nav-links open" : "nav-links"}>
        {navLinks.map((link) => (
          <a key={link.href} href={link.href} onClick={closeMenu}>
            {link.label}
          </a>
        ))}
      </nav>

       {isLoggedIn ? (
                 <Tooltip title={<Typography fontSize={14}>Logout</Typography>}>
  <Box
    onClick={logout}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      cursor: "pointer",
      px: 2,
      py: 1,
      borderRadius: "14px",
      background: "rgba(0,120,201,0.08)",
      color: "#0078c9",
      transition: "0.3s",
      "&:hover": {
        background: "#9e0b22",
        color: "#fff",
        transform: "translateY(-2px)",
      },
    }}
  >
    <LogoutIcon sx={{ fontSize: 23 }} />
    <Typography
      sx={{
        fontSize: "13px",
        fontWeight: 700,
        letterSpacing: "0.5px",
      }}
    >
      Logout
    </Typography>
  </Box>
</Tooltip>
                    ):(

      <div className="header-actions">
        <a className="primary-btn small" href="/login"   rel="noreferrer">
          Get Started
        </a>
        <button className="menu-btn" onClick={() => setOpen((prev) => !prev)} aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
                  )}
    </header>
  );
}

const logoBoxStyle = {
  width: 88,
  height: 88,
  borderRadius: "26px",
  background: "linear-gradient(135deg, #ffffff, #f4f9ff)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 16px 38px rgba(6,29,52,0.10)",
  border: "1px solid rgba(0,120,201,0.10)",
};

export default function OptimizeXP(props) {
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const handleDemoChange = (event) => {
    const { name, value } = event.target;
    setDemoForm((current) => ({ ...current, [name]: value }));
  };
    const [isLoggedIn, setIsLoggedIn] = useState(
    !!sessionStorage.getItem("usertoken")
  );

const handleDemoSubmit = async () => {
  try {
    const response = await fetch(`${baseURL}/request-demo`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...demoForm,
        to: "safiyanaaz@optimizegs.com",
      }),
    });

    if (response.ok) {
      alert("Demo Request for SelexMB Submitted Successfully");
      setDemoOpen(false)
    } else {
      alert("Failed to submit request");
    }
  } catch (error) {
    console.error(error);
    alert("Something went wrong");

  }
};

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box
    className="modern-company-page"
      sx={{
        minHeight: "100vh",
        bgcolor: "#fff",
        color: navy,
        overflowX: "hidden",
      }}
    >
            <style>{styles}</style>


    
           <Header  isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />

  

      <Box
        component="main"
        id="top"
        sx={{
          position: "relative",
          overflow: "hidden",
          background:
            "radial-gradient(circle at 88% 4%, rgba(0,120,201,0.11), transparent 28%), #fff",
        }}
      >
        <Box
          sx={{
            width: "min(1280px, calc(100% - 32px))",
            mx: "auto",
            pt: { xs: 10, md: 15 },
            pb: { xs: 2, md: 3 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.08fr 0.92fr" },
            gap: { xs: 5, md: 7 },
            alignItems: "center",
          }}
        >
          <Box>
          
            <Typography
               component="h1"
                          sx={{
                            fontSize: { xs: 40, sm: 54, md: 72 },
                lineHeight: 0.96,
                fontWeight: 950,
                letterSpacing: { xs: "-1.8px", md: "-3.4px" },
                color: navy,
                mb: 2,
                mt:2
                          }}
            >
              Industrial AI, engineered for the{" "}
              <Box component="span" sx={{ color: primary }}>plant.</Box>
            </Typography>

            <Typography
             sx={{
                fontSize: { xs: 15, md: 18 },
                color: "#000",
                lineHeight: 1.8,
                maxWidth: 750,
                mb: 3.5,
              }}
             
            >
              OptimizeXP combines machine-learning operations, neural networks,
              and deep process-domain knowledge to deliver software that runs on
              real units across oil, gas, and refining operations.
            </Typography>
  <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={() => setDemoOpen(true)}
                sx={{
                  px: 4,
                  py: 1.4,
                  borderRadius: "16px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 15,
                  border: `1px solid ${primary}`,
                  color: primary,

                  "&:hover": {
                    borderColor: accent,
                    color: accent,
                    bgcolor: "rgba(158,11,34,0.04)",
                  },
                }}
              >
                Request Demo
              </Button>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: "34px",
              background: cardBackground,
              border,
              boxShadow: "0 24px 60px rgba(0,120,201,0.10)",
            }}
          >
            <Typography sx={sectionLabelSx}>Platform coverage</Typography>
            <Typography
              sx={{
                fontSize: { xs: 34, md: 46 },
                fontWeight: 950,
                lineHeight: 1,
                color: primary,
                mb: 3,
              }}
            >
              Industrial Intelligence Ecosystem
            </Typography>

            <Box sx={{ display: "grid", gap: 1.5 }}>
              {[
                ["AI + MLOps", "Build, validate, deploy, and sustain industrial AI models."],
                ["Simulation", "Combine rigorous engineering models with live operational data."],
                ["Risk Intelligence", "Connect process safety studies to practical operating decisions."],
              ].map(([title, description]) => (
                <Paper
                  key={title}
                  elevation={0}
                 sx={{
                      p: 2,
                      borderRadius: "18px",

                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.92), rgba(248,251,255,0.88))",

                      border:
                        "1px solid rgba(0,120,201,0.12)",

                      boxShadow:
                        "0 10px 28px rgba(0,120,201,0.06)",
                    }}
                >
                  <Typography  sx={{
                        fontWeight: 900,
                        color: primary,
                        mb: 0.4,
                        fontSize: 16,
                      }}>
                    {title}
                  </Typography>
                  <Typography   sx={{
                        fontSize: 14,
                        color: "#000",
                        lineHeight: 1.6,
                      }}>
                    {description}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    

      <Box
        component="section"
        id="solutions"
        sx={{ width: "min(1180px, calc(100% - 32px))", mx: "auto", py: { xs: 1, md: 2  } }}
      >
           <Paper
            elevation={0}
            sx={{
              overflow: "hidden",
              borderRadius: "28px",
              border,
              boxShadow: "0 24px 60px rgba(0,120,201,0.12)",
            }}
          >
            <Box
              sx={{
                minHeight: 340,
                display: "grid",
                placeItems: "center",
                p: 3,
                background:
                  "radial-gradient(circle at 25% 10%, rgba(0,120,201,0.13), transparent 38%), linear-gradient(180deg,#fbfdff,#eef7ff)",
              }}
            >
              <Paper
                elevation={0}
                sx={{ p: 4, textAlign: "center", borderRadius: "22px", border, bgcolor: "rgba(255,255,255,0.92)" }}
              >
              <Box
                 component="img"
                 src={selexpyramis1}
                 sx={{
                   width: 1000,
                   height: "500px",
                   objectFit: "contain",
                 }}
               />
              </Paper>
            </Box>
          </Paper>


        <Box sx={{ maxWidth: 760, mb: 6  ,mt:4}}>
          <Typography sx={sectionLabelSx}>Solutions</Typography>
          <Typography
            component="h2"
            sx={{
              fontSize: 16,
              fontWeight: 950,
              letterSpacing: "1.2px",
              lineHeight: 1.05,
              color: navy,
              mb: 2,
            }}
          >
            Applied AI for process, reliability, and safety.
          </Typography>
          <Typography sx={{ color: bodyText, lineHeight: 1.8, fontSize: 16 }}>
            Each solution answers a question your engineers may not have and is
            grounded in process fundamentals rather than generic analytics.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
            gap: 3,
          }}
        >
          {solutions.map((solution) => (
            <Paper
              key={solution.title}
              elevation={0}
              sx={{
                p: 3.5,
                minHeight: 210,
                borderRadius: "28px",
                background: cardBackground,
                border,
                boxShadow: "0 16px 42px rgba(0,120,201,0.08)",
                transition: "0.35s",
                "&:hover": {
                  transform: "translateY(-8px)",
                  borderColor: primary,
                  boxShadow: "0 24px 58px rgba(0,120,201,0.14)",
                },
              }}
            >
              
              <Typography sx={{ fontSize: 22, fontWeight: 950, color: primary, mb: 1.2 }}>
                {solution.title}
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#000", lineHeight: 1.8 }}>
                {solution.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>

      <Box component="section" id="platform" sx={{ bgcolor: "#f8fbff", py: { xs: 1, md: 4 } }}>
        <Box sx={{ width: "min(1280px, calc(100% - 32px))", mx: "auto" }}>
          <Box sx={{ textAlign: "center", maxWidth: 780, mx: "auto", mb: 6 }}>
            <Typography sx={sectionLabelSx}>The SelexSuite Platform</Typography>
            <Typography
              component="h2"
              sx={{ fontSize:16, fontWeight: 950, color: navy, letterSpacing: "1.2px", mb: 2 }}
            >
              One platform. The full operation.
            </Typography>
            <Typography sx={{ color: bodyText, lineHeight: 1.8, fontSize: 16 }}>
              Three product families cover data intelligence, simulation and optimization,
              and process-safety risk across the plant lifecycle.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 3,
              mb: 5,
            }}
          >
            {platforms.map((platform) => (
              <Paper
                key={platform.code}
                elevation={0}
                sx={{
                  p: 3.5,
                  borderRadius: "28px",
                  background: cardBackground,
                  border,
                  boxShadow: "0 16px 42px rgba(0,120,201,0.08)",
                  transition: "0.35s",
                  "&:hover": { transform: "translateY(-8px)", borderColor: primary },
                }}
              >
                 <Box sx={logoBoxStyle}>
                  <Box component="img" src={platform.logo} alt={platform.name} sx={{ width: 66, maxHeight: 66, objectFit: "contain" }} />
                </Box>
               
                 <Typography sx={{ fontSize: 25, fontWeight: 950, color: primary, mb: 1 , mt:1 }}>
                                   {platform.code}
                  </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 950, color: navy, mb: 1.2 }}>
                  {platform.title}
                </Typography>
                <Typography sx={{ color: bodyText, fontSize: 14, lineHeight: 1.75, mb: 2.5 }}>
                  {platform.description}
                </Typography>
                <Box sx={{ display: "grid", gap: 1.2 }}>
                  {platform.items.map((item) => (
                    <Box key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <VerifiedIcon sx={{ color: primary, fontSize: 18, mt: 0.15 }} />
                      <Typography sx={{ fontSize: 14, color: navy }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>

         
        </Box>
      </Box>

      <Box
        component="section"
        id="approach"
        sx={{ width: "min(1280px, calc(100% - 32px))", mx: "auto", py: { xs: 3, md: 4 } }}
      >
        <Box sx={{ maxWidth: 760, mb: 6 }}>
          <Typography sx={sectionLabelSx}>Our Approach</Typography>
          <Typography
            component="h2"
            sx={{ fontSize: 16, fontWeight: 950, color: navy, letterSpacing: "1.2px", mb: 2 }}
          >
            From domain knowledge to deployed intelligence.
          </Typography>
          <Typography sx={{ color: bodyText, lineHeight: 1.8, fontSize: 16 }}>
            A repeatable path from what your engineers know to software running on
            the unit and staying accurate as plant conditions change.
          </Typography>
        </Box>

        
      </Box>
      <Box
        component="section"
        id="contact"
        sx={{
          py: { xs: 3, md: 4 },
          textAlign: "center",
          background: "radial-gradient(circle at 50% 50%, rgba(0,120,201,0.10), transparent 38%), #f8fbff",
        }}
      >
        <Box sx={{ width: "min(780px, calc(100% - 32px))", mx: "auto" }}>
          <Typography sx={sectionLabelSx}>Get in touch</Typography>
          <Typography
            sx={{ fontSize: 16, fontWeight: 950, color: navy, letterSpacing: "1.2px", lineHeight: 1.05, mb: 2 }}
          >
            Let&apos;s put AI to work on your unit.
          </Typography>
          <Typography sx={{ color: bodyText, fontSize: { xs: 16, md: 18 }, lineHeight: 1.8, mb: 4 }}>
            Tell us the property you cannot measure quickly enough, the failure you
            keep chasing, or the margin you are leaving behind, and we will scope it.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
            <Button
              onClick={() => setDemoOpen(true)}
              variant="contained"
              sx={{
                  px: 4,
                  py: 1.4,
                  borderRadius: "16px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 15,
                  border: `1px solid ${primary}`,
                  color: "#fff",

                  "&:hover": {
                    borderColor: accent,
                    color: accent,
                    bgcolor: "rgba(158,11,34,0.04)",
                  },
                }}
            >
              Request a demo
            </Button>
           
          </Box>
        </Box>
      </Box>

           <Footer />


      <Modal open={demoOpen} onClose={() => setDemoOpen(false)}>
        <Box
          component="form"
          onSubmit={handleDemoSubmit}
          sx={{
            width: { xs: "calc(100% - 32px)", sm: 440 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "#fff",
            borderRadius: "26px",
            p: 3,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: "0 24px 70px rgba(6,29,52,0.24)",
            border,
          }}
        >
          <Typography sx={{ fontSize: 24, fontWeight: 950, color: primary, mb: 1 }}>
            Request Demo
          </Typography>
          <Typography sx={{ fontSize: 14, color: bodyText, mb: 2.5 }}>
            Fill in your details and your email application will open with the completed request.
          </Typography>

          {[
            { label: "Full Name", name: "name", required: true },
            { label: "Business Email", name: "email", required: true, type: "email" },
            { label: "Phone", name: "phone" },
            { label: "Company", name: "company" },
            { label: "Product", name: "product" },
            { label: "Message", name: "message", multiline: true },
          ].map((field) => (
            <TextField
              key={field.name}
              fullWidth
              required={field.required}
              type={field.type || "text"}
              size="small"
              label={field.label}
              name={field.name}
              multiline={field.multiline}
              rows={field.multiline ? 4 : 1}
              value={demoForm[field.name]}
              onChange={handleDemoChange}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": { borderRadius: "12px", fontSize: 14 },
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primary },
                "& .MuiInputLabel-root.Mui-focused": { color: primary },
              }}
            />
          ))}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
            <Button
              onClick={() => setDemoOpen(false)}
              variant="outlined"
              sx={{ borderRadius: "12px", textTransform: "none", color: primary, borderColor: primary }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 800,
                background: "linear-gradient(90deg,#0078c9,#0d8fe6)",
                "&:hover": { background: accent },
              }}
            >
              Send Request
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}




const styles = `  
 @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  .modern-company-page {
    --primary: ${COLORS.primary};
    --accent: ${COLORS.accent};
    --navy: ${COLORS.navy};
    --dark: ${COLORS.dark};
    --muted: ${COLORS.muted};
    font-family: 'Inter', sans-serif;
    color: var(--dark);
    background: #ffffff;
    overflow-x: hidden;
    top:0px;
  }
.brand img { width: 150px; height: 58px; object-fit: contain; display: block; }
 .nav-links { display: flex; gap: 6px; align-items: center; }
  .nav-links a {
    text-decoration: none;
    color: #17324d;
    font-size: 14px;
    font-weight: 700;
    padding: 10px 12px;
    border-radius: 999px;
    transition: 0.25s ease;
  }
  .nav-links a:hover { background: rgba(0,120,201,0.10); color: var(--primary); }
    .header-actions { display: flex; align-items: center; gap: 10px; }
  .menu-btn {
    display: none;
    border: 0;
    border-radius: 14px;
    width: 44px;
    height: 44px;
    background: rgba(0,120,201,0.10);
    color: var(--primary);
    cursor: pointer;
  }
    .site-header {
    position: fixed;
    top: 0px;
    left: 50%;
    transform: translateX(-50%);
    width: min(1380px, calc(100% - 32px));
    min-height: 74px;
    padding: 10px 14px 10px 22px;
    border-radius: 24px;
    background: #fff;
    backdrop-filter: blur(18px);
    box-shadow: 0 18px 50px rgba(8, 37, 68, 0.14);
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 1000;
  }

  .primary-btn, .secondary-btn, .outline-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 800;
    text-decoration: none;
    transition: 0.25s ease;
  }

  .primary-btn {
    color: white;
    background: linear-gradient(135deg, #0078c9, #005da0);
    padding: 14px 22px;
    box-shadow: 0 14px 30px rgba(0,120,201,0.30);
  }
  .primary-btn:hover {color:white;background:#9e0b22; transform: translateY(-2px); box-shadow: 0 18px 40px rgba(0,120,201,0.38); }
  .primary-btn.small { padding: 12px 18px; }
`